import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import AppShell from "../shell/AppShell.jsx";
import DetailDrawer from "../components/DetailDrawer.jsx";
import { useAssignmentOnboarding } from "./useAssignmentOnboarding.js";
import { bindAssignmentDevice, bindAssignmentSim, markAssignmentReadyForMobileHandover } from "./assignmentOnboardingApi.js";
import "./assignmentOnboarding.css";
import "../pages/states.css";

import PageSkeleton from "../components/Skeleton.jsx";
const CRUMBS = ["Backoffice", "Thailand Jobs", "Assignments", "Onboarding"];
const DRAWER_TITLE_ID = "onb-drawer-title";

const HERO_COPY_TEXT = "เตรียมอุปกรณ์และซิมสำหรับแรงงานหลังจับคู่งานแล้ว แต่ยังไม่เริ่ม Heartbeat / Location Tracking";
const TOP_WARNING_TITLE = "ยังไม่เริ่มติดตามตำแหน่งและสัญญาณจากเครื่องของแรงงาน";
const TOP_WARNING_BODY = "หน้านี้เตรียมอุปกรณ์ (Device) และซิม (SIM) ให้แรงงานเท่านั้น ไม่มีการติดตามตำแหน่ง ไม่มี geofence และไม่มีระบบจดจำใบหน้าในหน้าจอนี้";
const DEVICE_WARNING_1 = "MAC/IMEI ใช้เป็นสัญญาณประกอบ ไม่ใช่ตัวตนเดียวของแรงงาน";
const DEVICE_WARNING_2 = "การผูกอุปกรณ์นี้ยังไม่เริ่ม Tracking";
const SIM_WARNING_1 = "เบอร์และ SIM Serial ต้องถูกบันทึกแบบตรวจสอบได้ และแสดงผลแบบปิดบังในหน้ารายละเอียด";
const SIM_WARNING_2 = "ยังไม่เริ่ม Heartbeat / Location Tracking";
const HANDOVER_WARNING_1 = "พร้อมส่งมอบ Mobile App ให้แรงงาน แต่ยังไม่เริ่ม Tracking";
const HANDOVER_WARNING_2 = "การติดตามจะเริ่มเมื่อเครื่องของแรงงานเริ่มส่งสัญญาณเข้ามา";
const NO_PERMISSION_OR_STATE_REASON = "คุณไม่มีสิทธิ์ หรือ Assignment นี้ไม่ได้อยู่ในสถานะที่สามารถทำรายการนี้ได้";

const ONBOARDING_STATUS_LABELS = {
  NotStarted: "ยังไม่เริ่ม",
  DevicePending: "รอดำเนินการ",
  DeviceBound: "ผูกอุปกรณ์แล้ว",
  SimBound: "ผูกซิมแล้ว",
  ReadyForMobileHandover: "พร้อมส่งมอบ Mobile App",
};

const ONBOARDING_STATUS_BADGE_VARIANT = {
  NotStarted: "neutral",
  DevicePending: "neutral",
  DeviceBound: "info",
  SimBound: "warning",
  ReadyForMobileHandover: "success",
};

function onboardingStatusLabel(status) {
  return ONBOARDING_STATUS_LABELS[status] ?? status ?? "-";
}

function onboardingStatusBadgeVariant(status) {
  return ONBOARDING_STATUS_BADGE_VARIANT[status] ?? "neutral";
}

function describeLoadError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์ดูข้อมูล Onboarding ของ Assignment นี้";
  }
  if (error?.status === 404) {
    return "ไม่พบ Assignment นี้ อาจถูกย้ายแล้ว";
  }
  return "ไม่สามารถโหลดข้อมูล Onboarding ได้ กรุณาลองใหม่อีกครั้ง";
}

function describeDeviceError(error) {
  if (error?.validation) {
    return "กรุณากรอก Device Fingerprint ให้ครบถ้วน";
  }
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์ผูกอุปกรณ์สำหรับ Assignment นี้";
  }
  if (error?.status === 404) {
    return "ไม่พบ Assignment นี้ อาจถูกย้ายแล้ว";
  }
  if (error?.status === 409) {
    // ApiClientError.message is always the generic "Request failed with
    // status 409" (apiClient.js never parses/attaches the response body's
    // own message) - never trust it as user-facing copy here, same
    // precedent AssignmentsPage.describeCreateError already sets.
    return "ไม่สามารถผูกอุปกรณ์นี้ได้ — Assignment อาจไม่ได้อยู่ในสถานะ Active หรืออุปกรณ์นี้ถูกผูกกับแรงงานคนอื่นอยู่แล้ว กรุณาตรวจสอบแล้วลองใหม่อีกครั้ง";
  }
  if (error?.status === 400) {
    return "ข้อมูลไม่ถูกต้อง กรุณาตรวจสอบฟอร์มอีกครั้ง";
  }
  return "ไม่สามารถผูกอุปกรณ์ได้ กรุณาลองใหม่อีกครั้ง";
}

function describeSimError(error) {
  if (error?.validation) {
    return "กรุณากรอกเบอร์โทรศัพท์ให้ครบถ้วน";
  }
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์ผูกซิมสำหรับ Assignment นี้";
  }
  if (error?.status === 404) {
    return "ไม่พบ Assignment นี้ อาจถูกย้ายแล้ว";
  }
  if (error?.status === 409) {
    return "ไม่สามารถผูกซิมนี้ได้ — Assignment อาจไม่ได้อยู่ในสถานะ Active หรือซิมนี้ถูกผูกกับแรงงานคนอื่นอยู่แล้ว กรุณาตรวจสอบแล้วลองใหม่อีกครั้ง";
  }
  if (error?.status === 400) {
    return "ข้อมูลไม่ถูกต้อง กรุณาตรวจสอบฟอร์มอีกครั้ง";
  }
  return "ไม่สามารถผูกซิมได้ กรุณาลองใหม่อีกครั้ง";
}

function describeHandoverError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์ยืนยันความพร้อมส่งมอบสำหรับ Assignment นี้";
  }
  if (error?.status === 404) {
    return "ไม่พบ Assignment นี้ อาจถูกย้ายแล้ว";
  }
  if (error?.status === 409) {
    return "ยืนยันไม่สำเร็จ — ต้องผูกอุปกรณ์และซิมให้เรียบร้อยก่อน กรุณาตรวจสอบแล้วลองใหม่อีกครั้ง";
  }
  return "ไม่สามารถยืนยันความพร้อมส่งมอบได้ กรุณาลองใหม่อีกครั้ง";
}

function formatDateTimeTh(value) {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const datePart = date.toLocaleDateString("th-TH-u-ca-gregory", { day: "2-digit", month: "short", year: "numeric" });
  const timePart = date.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${datePart} · ${timePart} ICT`;
}

/**
 * /backoffice/thailand-jobs/assignments/:assignmentId/onboarding - the
 * Assignment Onboarding / Device / SIM Binding screen (Prompt 111B, built
 * from the W53 reference). Reachable only from an assignment's own detail
 * drawer ("เปิด Onboarding") - there is no sidebar item for this screen and
 * no cross-assignment list, because the real Prompt 110 API has only an
 * assignment-scoped detail endpoint, never a list/summary endpoint (see
 * assignmentOnboardingApi.js's own doc comment). This screen never calls a
 * heartbeat/location/tracking/geofence/face-recognition API (none of those
 * exist anywhere in this codebase), and canStartTracking is always
 * rendered as blocked, matching AssignmentOnboardingNextActionsDto's own
 * always-false contract. No raw deviceFingerprint/macAddress/imei/
 * phoneNumber/simSerial is ever rendered - only the *Masked fields the
 * real API already returns.
 */
export default function AssignmentOnboardingPage() {
  const { assignmentId } = useParams();
  const { status, data, error, refetch } = useAssignmentOnboarding(assignmentId);

  const [drawerView, setDrawerView] = useState(null); // null | "device" | "sim" | "handover"
  const [deviceForm, setDeviceForm] = useState({ deviceFingerprint: "", macAddress: "", imei: "", appVersion: "", note: "" });
  const [deviceSubmit, setDeviceSubmit] = useState({ status: "idle", error: null });
  const [simForm, setSimForm] = useState({ phoneNumber: "", carrierName: "", simSerial: "", note: "" });
  const [simSubmit, setSimSubmit] = useState({ status: "idle", error: null });
  const [handoverSubmit, setHandoverSubmit] = useState({ status: "idle", error: null });

  function openDeviceDrawer() {
    setDeviceForm({ deviceFingerprint: "", macAddress: "", imei: "", appVersion: "", note: "" });
    setDeviceSubmit({ status: "idle", error: null });
    setDrawerView("device");
  }

  function openSimDrawer() {
    setSimForm({ phoneNumber: "", carrierName: "", simSerial: "", note: "" });
    setSimSubmit({ status: "idle", error: null });
    setDrawerView("sim");
  }

  function openHandoverDrawer() {
    setHandoverSubmit({ status: "idle", error: null });
    setDrawerView("handover");
  }

  function closeDrawer() {
    setDrawerView(null);
  }

  async function handleDeviceSubmit(event) {
    event.preventDefault();
    if (!deviceForm.deviceFingerprint.trim()) {
      setDeviceSubmit({ status: "error", error: { validation: true } });
      return;
    }
    setDeviceSubmit({ status: "submitting", error: null });
    try {
      await bindAssignmentDevice(assignmentId, {
        deviceFingerprint: deviceForm.deviceFingerprint.trim(),
        macAddress: deviceForm.macAddress.trim() || null,
        imei: deviceForm.imei.trim() || null,
        platform: "Android",
        appPackageId: "com.innovancex.workmatex",
        appVersion: deviceForm.appVersion.trim() || null,
        note: deviceForm.note.trim() || null,
      });
      setDeviceSubmit({ status: "success", error: null });
      refetch();
      closeDrawer();
    } catch (submitError) {
      setDeviceSubmit({ status: "error", error: submitError });
    }
  }

  async function handleSimSubmit(event) {
    event.preventDefault();
    if (!simForm.phoneNumber.trim()) {
      setSimSubmit({ status: "error", error: { validation: true } });
      return;
    }
    setSimSubmit({ status: "submitting", error: null });
    try {
      await bindAssignmentSim(assignmentId, {
        phoneNumber: simForm.phoneNumber.trim(),
        carrierName: simForm.carrierName.trim() || null,
        simSerial: simForm.simSerial.trim() || null,
        note: simForm.note.trim() || null,
      });
      setSimSubmit({ status: "success", error: null });
      refetch();
      closeDrawer();
    } catch (submitError) {
      setSimSubmit({ status: "error", error: submitError });
    }
  }

  async function handleHandoverSubmit() {
    setHandoverSubmit({ status: "submitting", error: null });
    try {
      await markAssignmentReadyForMobileHandover(assignmentId);
      setHandoverSubmit({ status: "success", error: null });
      refetch();
      closeDrawer();
    } catch (submitError) {
      setHandoverSubmit({ status: "error", error: submitError });
    }
  }

  const isInitialLoading = status === "loading" || status === "idle";
  const isInitialError = status === "error";

  if (isInitialLoading) {
    return (
      <AppShell crumbs={CRUMBS}>
        <PageSkeleton variant="table" toolbar={false} label="กำลังโหลดข้อมูล Onboarding..." />
      </AppShell>
    );
  }

  if (isInitialError) {
    return (
      <AppShell crumbs={CRUMBS}>
        <div className="wx-state-card wx-state-card--danger" role="alert">
          <h1 className="wx-state-title">ไม่สามารถโหลดข้อมูล Onboarding ได้</h1>
          <p className="wx-state-body">{describeLoadError(error)}</p>
          <button type="button" className="wx-button wx-button--primary" onClick={refetch}>
            ลองใหม่อีกครั้ง
          </button>
        </div>
      </AppShell>
    );
  }

  const nextActions = data.nextActions ?? {};
  const device = data.device ?? null;
  const sim = data.sim ?? null;
  const isReadyForHandover = data.onboardingStatus === "ReadyForMobileHandover";

  return (
    <AppShell crumbs={CRUMBS}>
      <div className="wx-onb-page">
        <div className="wx-onb-toolbar">
          <Link className="wx-onb-back-link" to="/backoffice/thailand-jobs/assignments">
            กลับไปที่ Assignments
          </Link>
          <div className="wx-onb-toolbar-actions">
            <button type="button" className="wx-button wx-button--ghost" onClick={refetch}>
              Refresh
            </button>
          </div>
        </div>

        <header className="wx-onb-head">
          <div className="wx-onb-title">
            <h1>
              Assignment Onboarding <span className="wx-onb-title-th">เตรียมความพร้อมก่อนส่งมอบอุปกรณ์</span>
            </h1>
            <p>{HERO_COPY_TEXT}</p>
          </div>
        </header>

        <div className="wx-onb-top-notice" role="status">
          <strong>{TOP_WARNING_TITLE}</strong>
          <span>{TOP_WARNING_BODY}</span>
        </div>

        <section className="wx-panel wx-onb-summary-panel">
          <div className="wx-onb-summary-head">
            <h2 className="wx-panel-title">สรุป Assignment</h2>
            <span className={`wx-badge wx-badge--${data.assignmentStatus === "Active" ? "success" : "danger"}`}>{data.assignmentStatus}</span>
          </div>
          <div className="wx-onb-summary-grid">
            <div className="wx-onb-summary-item">
              <label>Assignment</label>
              <strong className="wx-onb-code">{data.assignmentCode}</strong>
            </div>
            <div className="wx-onb-summary-item">
              <label>ผู้สมัคร (Worker)</label>
              <strong>
                {data.workerName ?? "-"} · {data.workerCode ?? "-"}
              </strong>
            </div>
            <div className="wx-onb-summary-item">
              <label>งาน</label>
              <strong>
                {data.jobTitle ?? "-"} · {data.jobCode ?? "-"}
              </strong>
            </div>
            <div className="wx-onb-summary-item">
              <label>นายจ้าง</label>
              <strong>{data.employerName ?? "-"}</strong>
            </div>
            <div className="wx-onb-summary-item">
              <label>จังหวัด / อำเภอ</label>
              <strong>
                {data.province ?? "-"} · {data.district ?? "-"}
              </strong>
            </div>
            <div className="wx-onb-summary-item">
              <label>Agent</label>
              <strong>
                {data.agentCode ?? "-"} · {data.agentName ?? "-"}
              </strong>
            </div>
            <div className="wx-onb-summary-item">
              <label>สถานะ Onboarding</label>
              <strong>
                <span className={`wx-badge wx-badge--${onboardingStatusBadgeVariant(data.onboardingStatus)}`}>{onboardingStatusLabel(data.onboardingStatus)}</span>
              </strong>
            </div>
          </div>
        </section>

        <div className="wx-onb-metrics">
          <div className="wx-onb-metric">
            <span>สถานะ Onboarding</span>
            <strong>{onboardingStatusLabel(data.onboardingStatus)}</strong>
          </div>
          <div className="wx-onb-metric">
            <span>การผูกอุปกรณ์</span>
            <strong>{device?.bindingStatus ?? "ยังไม่ผูก"}</strong>
          </div>
          <div className="wx-onb-metric">
            <span>การผูกซิม</span>
            <strong>{sim?.bindingStatus ?? "ยังไม่ผูก"}</strong>
          </div>
          <div className="wx-onb-metric">
            <span>พร้อมส่งมอบ Mobile App</span>
            <strong className={isReadyForHandover ? "wx-onb-metric-value--ok" : undefined}>{isReadyForHandover ? "พร้อมแล้ว" : "ยังไม่พร้อม"}</strong>
          </div>
          <div className="wx-onb-metric wx-onb-metric--reserved">
            <span>Tracking</span>
            <strong>ยังไม่เริ่ม Tracking</strong>
            {/* The "สงวนไว้" pill under this is gone - "ยังไม่เริ่ม Tracking"
                above already states the position in plain words. */}
          </div>
        </div>

        <section className="wx-panel wx-onb-block">
          <div className="wx-onb-block-head">
            <h2 className="wx-panel-title">อุปกรณ์ (Device)</h2>
            <button
              type="button"
              className="wx-button wx-button--primary wx-onb-bind-device-button"
              disabled={!nextActions.canBindDevice}
              title={nextActions.canBindDevice ? undefined : NO_PERMISSION_OR_STATE_REASON}
              onClick={openDeviceDrawer}
            >
              ผูกอุปกรณ์
            </button>
          </div>
          {device ? (
            <div className="wx-onb-kv-grid">
              <div className="wx-onb-kv"><span>รหัสอุปกรณ์</span><strong className="wx-onb-code">{device.deviceCode ?? "-"}</strong></div>
              <div className="wx-onb-kv"><span>แพลตฟอร์ม</span><strong>{device.platform}</strong></div>
              <div className="wx-onb-kv"><span>Device Fingerprint</span><strong className="wx-onb-masked">{device.deviceFingerprintMasked ?? "-"}</strong></div>
              <div className="wx-onb-kv"><span>MAC Address</span><strong className="wx-onb-masked">{device.macAddressMasked ?? "ไม่ระบุ"}</strong></div>
              <div className="wx-onb-kv"><span>IMEI</span><strong className="wx-onb-masked">{device.imeiMasked ?? "ไม่ระบุ"}</strong></div>
              <div className="wx-onb-kv"><span>App Package</span><strong className="wx-onb-code">{device.appPackageId ?? "-"}</strong></div>
              <div className="wx-onb-kv"><span>App Version</span><strong>{device.appVersion ?? "ไม่ระบุ"}</strong></div>
              <div className="wx-onb-kv"><span>สถานะการผูก</span><strong><span className="wx-badge wx-badge--info">{device.bindingStatus}</span></strong></div>
              <div className="wx-onb-kv"><span>ผูกเมื่อ</span><strong>{formatDateTimeTh(device.boundAtUtc) ?? "-"}</strong></div>
            </div>
          ) : (
            <p className="wx-empty-list">ยังไม่ได้ผูกอุปกรณ์สำหรับ Assignment นี้</p>
          )}
        </section>

        <section className="wx-panel wx-onb-block">
          <div className="wx-onb-block-head">
            <h2 className="wx-panel-title">ซิม (SIM)</h2>
            <button
              type="button"
              className="wx-button wx-button--primary wx-onb-bind-sim-button"
              disabled={!nextActions.canBindSim}
              title={nextActions.canBindSim ? undefined : NO_PERMISSION_OR_STATE_REASON}
              onClick={openSimDrawer}
            >
              ผูก SIM
            </button>
          </div>
          {sim ? (
            <div className="wx-onb-kv-grid">
              <div className="wx-onb-kv"><span>รหัสการผูกซิม</span><strong className="wx-onb-code">{sim.simBindingId}</strong></div>
              <div className="wx-onb-kv"><span>เบอร์โทร</span><strong className="wx-onb-masked">{sim.phoneNumberMasked ?? "-"}</strong></div>
              <div className="wx-onb-kv"><span>เครือข่าย</span><strong>{sim.carrierName ?? "ไม่ระบุ"}</strong></div>
              <div className="wx-onb-kv"><span>SIM Serial</span><strong className="wx-onb-masked">{sim.simSerialMasked ?? "ไม่ระบุ"}</strong></div>
              <div className="wx-onb-kv"><span>สถานะการผูก</span><strong><span className="wx-badge wx-badge--success">{sim.bindingStatus}</span></strong></div>
              <div className="wx-onb-kv"><span>ผูกเมื่อ</span><strong>{formatDateTimeTh(sim.boundAtUtc) ?? "-"}</strong></div>
            </div>
          ) : (
            <p className="wx-empty-list">ยังไม่ได้ผูกซิมสำหรับ Assignment นี้</p>
          )}
        </section>

        <section className="wx-panel wx-onb-block">
          <div className="wx-onb-block-head">
            <h2 className="wx-panel-title">ความพร้อมส่งมอบ Mobile App</h2>
            <button
              type="button"
              className="wx-button wx-button--primary wx-onb-handover-button"
              disabled={!nextActions.canMarkReadyForMobileHandover}
              title={nextActions.canMarkReadyForMobileHandover ? undefined : NO_PERMISSION_OR_STATE_REASON}
              onClick={openHandoverDrawer}
            >
              พร้อมส่งมอบ Mobile App
            </button>
          </div>
          <p className="wx-onb-note-muted-left">ต้องผูกอุปกรณ์และผูกซิมให้เรียบร้อยก่อน จึงจะยืนยันความพร้อมส่งมอบ Mobile App ได้</p>
        </section>

        <section className="wx-panel wx-onb-block wx-onb-tracking-block">
          <h2 className="wx-panel-title">Tracking</h2>
          <div className="wx-onb-notice wx-onb-notice--warn">{nextActions.trackingReservedReason || TOP_WARNING_TITLE}</div>
          <p className="wx-onb-note-muted-left">canStartTracking = false เสมอ — ไม่มีปุ่มเริ่ม Tracking ในหน้านี้ ไม่มี geofence และไม่มีระบบจดจำใบหน้า</p>
        </section>
      </div>

      <DetailDrawer
        open={drawerView !== null}
        onClose={closeDrawer}
        labelledBy={DRAWER_TITLE_ID}
        selectableSelector=".wx-onb-bind-device-button, .wx-onb-bind-sim-button, .wx-onb-handover-button"
      >
        {drawerView === "device" && (
          <DeviceBindingView
            titleId={DRAWER_TITLE_ID}
            assignment={data}
            form={deviceForm}
            onChange={(field, value) => setDeviceForm((previous) => ({ ...previous, [field]: value }))}
            onSubmit={handleDeviceSubmit}
            onCancel={closeDrawer}
            submitState={deviceSubmit}
          />
        )}
        {drawerView === "sim" && (
          <SimBindingView
            titleId={DRAWER_TITLE_ID}
            assignment={data}
            form={simForm}
            onChange={(field, value) => setSimForm((previous) => ({ ...previous, [field]: value }))}
            onSubmit={handleSimSubmit}
            onCancel={closeDrawer}
            submitState={simSubmit}
          />
        )}
        {drawerView === "handover" && (
          <MobileHandoverReadyView
            titleId={DRAWER_TITLE_ID}
            assignment={data}
            device={device}
            sim={sim}
            onConfirm={handleHandoverSubmit}
            onCancel={closeDrawer}
            submitState={handoverSubmit}
          />
        )}
      </DetailDrawer>
    </AppShell>
  );
}

function DeviceBindingView({ titleId, assignment, form, onChange, onSubmit, onCancel, submitState }) {
  return (
    <form className="wx-onb-drawer" onSubmit={onSubmit}>
      <span className="wx-onb-drawer-kicker">DEVICE BINDING</span>
      <h2 id={titleId} className="wx-onb-drawer-title">
        ผูกอุปกรณ์ (Device Binding)
      </h2>
      <p className="wx-onb-drawer-sub">
        {assignment.assignmentCode} · {assignment.workerName}
      </p>

      <div className="wx-onb-notice wx-onb-notice--warn">{DEVICE_WARNING_1}</div>
      <div className="wx-onb-notice wx-onb-notice--warn">{DEVICE_WARNING_2}</div>

      <div className="wx-onb-field">
        <label>
          Device Fingerprint <b>*</b>
        </label>
        <input
          type="text"
          placeholder="เช่น fp-9a72e1c4b8f0..."
          value={form.deviceFingerprint}
          onChange={(event) => onChange("deviceFingerprint", event.target.value)}
        />
      </div>
      <div className="wx-onb-two">
        <div className="wx-onb-field">
          <label>MAC Address</label>
          <input type="text" placeholder="AA:BB:CC:DD:EE:FF (ไม่บังคับ)" value={form.macAddress} onChange={(event) => onChange("macAddress", event.target.value)} />
        </div>
        <div className="wx-onb-field">
          <label>IMEI</label>
          <input type="text" placeholder="14-16 หลัก (ไม่บังคับ)" value={form.imei} onChange={(event) => onChange("imei", event.target.value)} />
        </div>
      </div>
      <div className="wx-onb-two">
        <div className="wx-onb-field">
          <label>Platform</label>
          <input type="text" value="Android" disabled />
        </div>
        <div className="wx-onb-field">
          <label>App Version</label>
          <input type="text" placeholder="เช่น 0.1.0" value={form.appVersion} onChange={(event) => onChange("appVersion", event.target.value)} />
        </div>
      </div>
      <div className="wx-onb-field">
        <label>App Package ID</label>
        <input type="text" value="com.innovancex.workmatex" disabled />
      </div>
      <div className="wx-onb-field">
        <label>หมายเหตุ (Note)</label>
        <textarea placeholder="ไม่บังคับกรอก" value={form.note} onChange={(event) => onChange("note", event.target.value)} />
      </div>

      {submitState.status === "error" && <p className="wx-onb-form-message">{describeDeviceError(submitState.error)}</p>}

      <div className="wx-onb-drawer-foot">
        <button type="button" className="wx-button wx-button--ghost" onClick={onCancel}>
          ยกเลิก
        </button>
        <button type="submit" className="wx-button wx-button--primary" disabled={submitState.status === "submitting"}>
          {submitState.status === "submitting" ? "กำลังบันทึก..." : "บันทึกการผูกอุปกรณ์"}
        </button>
      </div>
    </form>
  );
}

function SimBindingView({ titleId, assignment, form, onChange, onSubmit, onCancel, submitState }) {
  return (
    <form className="wx-onb-drawer" onSubmit={onSubmit}>
      <span className="wx-onb-drawer-kicker">SIM BINDING</span>
      <h2 id={titleId} className="wx-onb-drawer-title">
        ผูกซิม (SIM Binding)
      </h2>
      <p className="wx-onb-drawer-sub">
        {assignment.assignmentCode} · {assignment.workerName}
      </p>

      <div className="wx-onb-notice wx-onb-notice--warn">{SIM_WARNING_1}</div>
      <div className="wx-onb-notice wx-onb-notice--warn">{SIM_WARNING_2}</div>

      <div className="wx-onb-field">
        <label>
          เบอร์โทรศัพท์ (Phone Number) <b>*</b>
        </label>
        <input
          type="text"
          placeholder="เช่น 0812345678 หรือ +66812345678"
          value={form.phoneNumber}
          onChange={(event) => onChange("phoneNumber", event.target.value)}
        />
      </div>
      <div className="wx-onb-two">
        <div className="wx-onb-field">
          <label>เครือข่าย (Carrier)</label>
          <input type="text" placeholder="เช่น AIS, DTAC, TRUE (ไม่บังคับ)" value={form.carrierName} onChange={(event) => onChange("carrierName", event.target.value)} />
        </div>
        <div className="wx-onb-field">
          <label>SIM Serial</label>
          <input type="text" placeholder="ไม่บังคับ" value={form.simSerial} onChange={(event) => onChange("simSerial", event.target.value)} />
        </div>
      </div>
      <div className="wx-onb-field">
        <label>หมายเหตุ (Note)</label>
        <textarea placeholder="ไม่บังคับกรอก" value={form.note} onChange={(event) => onChange("note", event.target.value)} />
      </div>

      {submitState.status === "error" && <p className="wx-onb-form-message">{describeSimError(submitState.error)}</p>}

      <div className="wx-onb-drawer-foot">
        <button type="button" className="wx-button wx-button--ghost" onClick={onCancel}>
          ยกเลิก
        </button>
        <button type="submit" className="wx-button wx-button--primary" disabled={submitState.status === "submitting"}>
          {submitState.status === "submitting" ? "กำลังบันทึก..." : "บันทึกการผูกซิม"}
        </button>
      </div>
    </form>
  );
}

function MobileHandoverReadyView({ titleId, assignment, device, sim, onConfirm, onCancel, submitState }) {
  const deviceBound = device?.bindingStatus === "Bound";
  const simBound = sim?.bindingStatus === "Bound";

  return (
    <div className="wx-onb-drawer">
      <span className="wx-onb-drawer-kicker">MOBILE HANDOVER READY</span>
      <h2 id={titleId} className="wx-onb-drawer-title">
        ยืนยันพร้อมส่งมอบ Mobile App
      </h2>
      <p className="wx-onb-drawer-sub">
        {assignment.assignmentCode} · {assignment.workerName}
      </p>

      <div className="wx-onb-context-pill">
        Assignment <strong>{assignment.assignmentCode}</strong> · ผู้สมัคร <strong>{assignment.workerName}</strong> · งาน{" "}
        <strong>
          {assignment.jobTitle} ({assignment.jobCode})
        </strong>
      </div>

      <section className="wx-onb-dsec">
        <h3>เงื่อนไขก่อนยืนยัน</h3>
        <div className="wx-onb-next-actions">
          <div className={`wx-onb-next-action-row${deviceBound ? " wx-onb-next-action-row--ok" : ""}`}>
            <span className={`wx-onb-check-dot${deviceBound ? " wx-onb-check-dot--ok" : ""}`} />
            ผูกอุปกรณ์แล้ว (Device Bound)
          </div>
          <div className={`wx-onb-next-action-row${simBound ? " wx-onb-next-action-row--ok" : ""}`}>
            <span className={`wx-onb-check-dot${simBound ? " wx-onb-check-dot--ok" : ""}`} />
            ผูกซิมแล้ว (SIM Bound)
          </div>
        </div>
      </section>

      <div className="wx-onb-notice wx-onb-notice--warn">{HANDOVER_WARNING_1}</div>
      <div className="wx-onb-notice wx-onb-notice--warn">{HANDOVER_WARNING_2}</div>

      <p className="wx-onb-note-muted-left">การยืนยันนี้เปลี่ยนเฉพาะ onboardingStatus เป็น ReadyForMobileHandover เท่านั้น ไม่เริ่มการติดตามอุปกรณ์ ไม่มี geofence และไม่มีระบบจดจำใบหน้า</p>

      {submitState.status === "error" && <p className="wx-onb-form-message">{describeHandoverError(submitState.error)}</p>}

      <div className="wx-onb-drawer-foot">
        <button type="button" className="wx-button wx-button--ghost" onClick={onCancel}>
          ย้อนกลับ
        </button>
        <button type="button" className="wx-button wx-button--primary" onClick={onConfirm} disabled={submitState.status === "submitting"}>
          {submitState.status === "submitting" ? "กำลังยืนยัน..." : "ยืนยันพร้อมส่งมอบ Mobile App"}
        </button>
      </div>
    </div>
  );
}
